import { toaster } from '@/components/ui/toast'
import type { CreateMessage, InsertMessageReaction, Message } from '@hvf/contracts'
import { userId } from '@stateApp'
import { createMutation, useQueryClient } from '@tanstack/solid-query'
import { createSignal } from 'solid-js'
import { generateParentPreviewText } from '../generateParentPreviewText'
import type { DisplayMessage } from './resources/getChannelHistory'
import { whenInsertMessage } from './resources/whenInsertMessage'
import { whenUpdateMessage } from './resources/whenUpdateMessage'
import { whenUpsertReaction } from './resources/whenUpsertReaction'

export function useUpsert() {
  const [editingMessage, setEditingMessage] = createSignal<Message | undefined>()
  const [replyMessage, setReplyMessage] = createSignal<DisplayMessage | undefined>()
  const queryClient = useQueryClient()

  // Mutations
  const insertMutation = createMutation(() => ({
    mutationFn: (message: CreateMessage) => whenInsertMessage(message),
    onSuccess: onSuccessfulMutation,
    onError: (error: any) => {
      toaster.create({
        title: 'Error Sending Message',
        description: error.message || 'There was an error sending your message. Please try again.',
        type: 'error',
      })
    }
  }))

  const updateMutation = createMutation(() => ({
    mutationFn: ({ updateData, originalData }: { updateData: Message, originalData: Message }) =>
      whenUpdateMessage(updateData, originalData),
    onSuccess: (updatedData, variables) => {
      onSuccessfulMutation()

      if (!updatedData) return

      // Manually update any matching infinite queries for this channel
      queryClient.setQueriesData(
        { queryKey: ['channelHistory', variables.originalData.channel_id] },
        (oldData: any) => {
          if (!oldData) return oldData

          return {
            ...oldData,
            pages: oldData.pages.map((page: any[]) =>
              page.map((msg) =>
                // Merge the new data, preserving existing nested relations (like profile)
                msg.id === updatedData.id ? { ...msg, ...updatedData } : msg
              )
            ),
          }
        }
      )
    },
    onError: (error: any) => {
      toaster.create({
        title: 'Error Updating Message',
        description: error.message || 'There was an error updating your message. Please try again.',
        type: 'error',
      })
    }
  }))

  const reactionMutation = createMutation(() => ({
    mutationFn: (reaction: InsertMessageReaction) => whenUpsertReaction(reaction),
    onError: (error: any) => {
      console.error('[reactionMutation] Failed:', error)
      toaster.create({
        title: 'Reaction Failed',
        description: 'Could not update reaction. Please try again.',
        type: 'error',
      })
    }
  }))

  function onSuccessfulMutation() {
    setEditingMessage(undefined)
    setReplyMessage(undefined)
  }

  const send = (content: string, channel_id: string) => {
    if (content === '<p></p>' || content === '') return

    const parent_id = replyMessage()?.id || null
    const thread_id = replyMessage() ? (replyMessage()?.thread_id ?? replyMessage()?.id) : null
    const parent_preview_text = replyMessage()?.content
      ? generateParentPreviewText(replyMessage()!.content!)
      : null

    const editing = editingMessage()

    if (editing) {
      updateMutation.mutate({
        updateData: {
          ...editing,
          content,
          parent_preview_text,
          parent_id,
          thread_id: thread_id ?? null,
        },
        originalData: editing
      })
    } else {
      insertMutation.mutate({
        channel_id,
        created_by: userId()!,
        content,
        parent_preview_text,
        parent_id,
        thread_id,
      })
    }
  }

  const sendIsLoading = () => insertMutation.isPending || updateMutation.isPending

  return {
    editingMessage,
    setEditingMessage,
    replyMessage,
    setReplyMessage,
    send,
    sendIsLoading,
    reactionMutation,
  }
}
